/*
 * sp-before-after.js — comparador "Antes y después" (sections/sp-before-after.liquid; kit Shopify maker, 2026-10-17;
 * CONTRACTS §C). window.spBeforeAfter = { init(frame), destroy(frame), get(frame), initAll(scope), destroyAll(scope) }: una
 * instancia por marco [data-sp-ba] (WeakMap), con auto-init y soporte del editor de temas (shopify:section:load/unload).
 *
 * - Una sola variable de posición (--sp-ba-pos en %, en el marco): el CSS recorta la foto de antes con clip-path y mueve la
 *   barra con transform. Nada cambia el layout (sin CLS).
 * - Puntero: pointer events PASIVOS sobre el marco (nunca en window, document ni body; sin preventDefault). Con mouse,
 *   apretar ya mueve la barra; con el dedo, el marco tiene touch-action: pan-y pinch-zoom: si el gesto es vertical lo toma el
 *   navegador (scroll; llega pointercancel) y si es horizontal se arrastra (setPointerCapture). Un toque sin arrastre lleva
 *   la barra a ese punto.
 * - Teclado y lectores de pantalla: el <input type="range"> (flechas, Re Pág/Av Pág, Inicio, Fin) mueve lo mismo por su
 *   evento input; aria-valuetext dice el porcentaje.
 * - Pista: la primera vez que la mitad del marco entra en pantalla, un solo barrido suave (inicio → +15 → −15 → inicio,
 *   ~1,6 s) con requestAnimationFrame sólo durante el barrido; si sale de pantalla antes de terminar, vuelve al inicio y
 *   corta. Nunca con prefers-reduced-motion ni si la persona ya tocó el comparador. Sin loops.
 */
(function () {
  'use strict';
  if (window.spBeforeAfter && window.spBeforeAfter.__kit) return;

  var SEL = '[data-sp-ba]';
  var instances = new WeakMap();
  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var HINT_MS = 1600;
  var HINT_AMP = 15;
  // umbrales de a 5 % y "se ve" alcanzable: en un bloque más alto que la pantalla la proporción pedida nunca entraría
  var STEPS = [];
  for (var s = 0; s <= 10; s++) STEPS.push(s / 20);
  function seen(e, want) {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var h = (e.boundingClientRect && e.boundingClientRect.height) || 1;
    return e.isIntersecting && e.intersectionRatio >= Math.min(want, (vh * 0.8) / h);
  }

  var clamp = function (p) { return Math.max(0, Math.min(100, Number(p) || 0)); };
  // suave a la entrada y a la salida de cada tramo
  var ease = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };

  function Compare(frame) {
    this.frame = frame;
    this.range = frame.querySelector('.sp-ba__range');
    this.start = clamp(frame.getAttribute('data-start') || 50);
    this.pos = this.start;
    this.drag = null;
    this.touched = false;
    this.rafId = 0;
    this.timer = 0;
    this.io = null;
    this.bound = [];
    this._hint = this.hintFrame.bind(this);
  }

  Compare.prototype.on = function (target, type, fn, options) {
    if (!target) return;
    target.addEventListener(type, fn, options);
    this.bound.push([target, type, fn, options]);
  };

  Compare.prototype.set = function (p) {
    p = clamp(p);
    this.pos = p;
    this.frame.style.setProperty('--sp-ba-pos', (Math.round(p * 100) / 100) + '%');
    if (this.range) {
      var v = Math.round(p);
      if (String(this.range.value) !== String(v)) this.range.value = String(v);
      this.range.setAttribute('aria-valuetext', v + ' %');
    }
  };

  Compare.prototype.fromX = function (x) {
    var r = this.frame.getBoundingClientRect();
    if (r.width > 0) this.set(((x - r.left) / r.width) * 100);
  };

  Compare.prototype.touch = function () {
    // si la pista estaba barriendo, la barra vuelve al inicio (un toque que termina en scroll no la deja corrida); el
    // arrastre y el teclado la mueven enseguida. Sin pista en curso, queda donde la dejó la persona
    var running = !!(this.rafId || this.timer);
    this.touched = true;
    this.stopHint(running);
  };

  Compare.prototype.startListeners = function () {
    var self = this;
    var frame = this.frame;
    var passive = { passive: true };
    // el arrastre lo hace el <input type="range"> nativo que cubre el marco (2026-10-18: en iOS Safari los pointer
    // events sobre el marco se cancelaban al primer movimiento); acá sólo se corta la pista y se marca el arrastre
    this.on(frame, 'pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      self.touch();
      self.drag = { id: e.pointerId };
      frame.classList.add('is-dragging');
    }, passive);
    var end = function (e) {
      var d = self.drag;
      if (!d || e.pointerId !== d.id) return;
      self.drag = null;
      frame.classList.remove('is-dragging');
    };
    this.on(frame, 'pointerup', end, passive);
    this.on(frame, 'pointercancel', end, passive);
    if (this.range) {
      // llegar con Tab corta la pista: la primera tecla mueve la barra desde donde está
      this.on(this.range, 'focus', function () { self.touch(); }, passive);
      this.on(this.range, 'input', function () {
        self.touch();
        self.set(self.range.value);
      }, passive);
    }
  };

  Compare.prototype.startHint = function () {
    if (reduced || typeof IntersectionObserver !== 'function') return;
    var self = this;
    this.io = new IntersectionObserver(function (entries) {
      var e = entries[entries.length - 1];
      // se ve con la mitad del marco adentro (o lo que entre si es más alto que la pantalla); el umbral 0 avisa cuando sale
      var vis = seen(e, 0.5);
      if (self.touched) { self.stopHint(false); return; }
      if (vis && !self.hintT0 && !self.timer) {
        self.timer = setTimeout(function () {
          self.timer = 0;
          if (self.touched) return;
          self.hintT0 = 0;
          self.rafId = requestAnimationFrame(self._hint);
        }, 250);
      } else if (!vis && (self.rafId || self.timer)) {
        self.stopHint(true); // salió de pantalla en el medio: vuelve al inicio y no se repite
      }
    }, { threshold: STEPS });
    this.io.observe(this.frame);
  };

  Compare.prototype.hintFrame = function (now) {
    this.rafId = 0;
    if (!this.hintT0) this.hintT0 = now;
    var t = Math.min(1, (now - this.hintT0) / HINT_MS);
    // tres tramos: inicio → +A (30 %), +A → −A (40 %), −A → inicio (30 %)
    var a = this.start;
    var p;
    if (t < 0.3) p = a + HINT_AMP * ease(t / 0.3);
    else if (t < 0.7) p = a + HINT_AMP - 2 * HINT_AMP * ease((t - 0.3) / 0.4);
    else p = a - HINT_AMP + HINT_AMP * ease((t - 0.7) / 0.3);
    this.set(p);
    if (t < 1) this.rafId = requestAnimationFrame(this._hint);
    else this.stopHint(true);
  };

  /** Corta la pista (para siempre). reset = vuelve la barra al inicio. */
  Compare.prototype.stopHint = function (reset) {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    if (this.timer) clearTimeout(this.timer);
    this.timer = 0;
    if (this.io) { this.io.disconnect(); this.io = null; }
    this.hintT0 = -1;
    if (reset) this.set(this.start);
  };

  Compare.prototype.destroy = function () {
    this.stopHint(false);
    for (var i = 0; i < this.bound.length; i++) {
      var b = this.bound[i];
      b[0].removeEventListener(b[1], b[2], b[3]);
    }
    this.bound.length = 0;
    this.frame.classList.remove('is-dragging');
    instances.delete(this.frame);
  };

  function framesIn(node) {
    var out = [];
    var root = node && node.nodeType === 1 ? node : document.documentElement;
    if (root.matches(SEL)) out.push(root);
    var found = root.querySelectorAll(SEL);
    for (var i = 0; i < found.length; i++) out.push(found[i]);
    return out;
  }

  var api = {
    __kit: 1,
    init: function (frame) {
      if (!frame || frame.nodeType !== 1) return null;
      var inst = instances.get(frame);
      if (inst) return inst;
      inst = new Compare(frame);
      instances.set(frame, inst);
      inst.set(inst.start);
      // sin JS el range queda deshabilitado (no se anuncia un control que no mueve nada); acá se habilita
      if (inst.range) inst.range.disabled = false;
      inst.startListeners();
      inst.startHint();
      return inst;
    },
    destroy: function (frame) {
      var inst = frame ? instances.get(frame) : undefined;
      if (!inst) return false;
      inst.destroy();
      return true;
    },
    get: function (frame) { return frame ? instances.get(frame) : undefined; },
    initAll: function (scope) { var l = framesIn(scope); for (var i = 0; i < l.length; i++) api.init(l[i]); },
    destroyAll: function (scope) { var l = framesIn(scope); for (var i = 0; i < l.length; i++) api.destroy(l[i]); }
  };
  window.spBeforeAfter = api;

  document.addEventListener('shopify:section:unload', function (e) { api.destroyAll(e.target); });
  document.addEventListener('shopify:section:load', function (e) { api.initAll(e.target); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { api.initAll(); });
  else api.initAll();
})();
